import React from 'react';
import {
  Navigate,
  Route,
  Routes as ReactRoutes,
  useLocation,
  useParams,
} from 'react-router-dom';

import AuthGuard from './components/AuthGuard';
import { useEnvironment } from './contexts/EnvironmentContext';
import AdminLayout from './layouts/AdminLayout';
import { BuyersPage } from './pages/BuyersPage';
import { ChatsPage } from './pages/ChatsPage';
import { DashboardPage } from './pages/DashboardPage';
import { LoginPage } from './pages/LoginPage/LoginPage';
import { NotFoundPage } from './pages/NotFoundPage/NotFoundPage';
import { OrderGuideUploadPage } from './pages/OrderGuideUploadPage';
import { OrdersPage } from './pages/OrdersPage';
import { SsoPage } from './pages/SsoPage';
import { SuppliersPage } from './pages/SuppliersPage';
import { UsersPage } from './pages/UsersPage';
import { WorkflowsPage } from './pages/WorkflowsPage';
import { WorkspacesPage } from './pages/WorkspacesPage';

/**
 * Legacy-admin bookmarks use the singular /supplier/<id> path. Redirect them
 * to /suppliers/<id> preserving the rest of the path, query, and hash.
 */
const LegacySupplierRedirect: React.FC = () => {
  const params = useParams();
  const { search, hash } = useLocation();
  const rest = params['*'] ?? '';
  return (
    <Navigate
      to={`/suppliers${rest ? `/${rest}` : ''}${search}${hash}`}
      replace
    />
  );
};

export const Routes: React.FC = () => {
  // Team management is still being built against the chodusk dynenv. Hiding the
  // sidebar entry is not enough on its own — the URL stays reachable — so the
  // route itself only exists there.
  const { currentEnvironment } = useEnvironment();
  const teamManagementAvailable = currentEnvironment === 'chodusk';

  return (
    <ReactRoutes>
      <Route path="/login" element={<LoginPage />} />
      {/* Chats-first launch: the portal is currently a chat tool, so root lands on chats */}
      <Route path="/" element={<Navigate to="/chats" replace />} />
      <Route
        path="/"
        element={
          <AuthGuard>
            <AdminLayout />
          </AuthGuard>
        }
      >
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="users/*" element={<UsersPage />} />
        <Route path="buyers/*" element={<BuyersPage />} />
        <Route path="chats/*" element={<ChatsPage />} />
        <Route path="suppliers/*" element={<SuppliersPage />} />
        <Route
          path="workspaces/*"
          element={
            teamManagementAvailable ? (
              <WorkspacesPage />
            ) : (
              <Navigate to="/dashboard" replace />
            )
          }
        />
        <Route path="supplier/*" element={<LegacySupplierRedirect />} />
        <Route path="orders/*" element={<OrdersPage />} />
        <Route path="sso/*" element={<SsoPage />} />
        <Route path="workflows" element={<WorkflowsPage />} />
        <Route
          path="workflows/order-guide-upload"
          element={<OrderGuideUploadPage />}
        />
        {/* /settings removed — it was a coming-soon placeholder; deep links land on the dashboard */}
        <Route path="settings" element={<Navigate to="/dashboard" replace />} />
      </Route>

      {/* 404 route */}
      <Route path="*" element={<NotFoundPage />} />
    </ReactRoutes>
  );
};
